/**
 * Robin Hood — „Robin Hood vs. Bigfoot".
 *
 * HIT AND RUN: po útoku smie útočiaca figúrka (Robin aj Outlaw) ísť až o 2
 * priestory — až po efektoch kariet. Outlaws majú po 1 zdraví a karta
 * Defenders of Sherwood ich vracia do hry.
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, dealDamage, discardRandom, drawCard, getMap } from '../ops.js';
import { areEnemies } from '../players.js';
import { draw, feint, regroup } from './common.js';
import { askMove, fighterOrNull, opponentOf } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const ROBIN = 'robin-hood';
const OUTLAW = 'outlaws';
const STEAL = 'robin-hood.steal-from-the-rich';

/**
 * „Your opponent discards 1 random card. Add its BOOST value to this card's
 * attack value." — odhodenie mení stav, preto v `duringChoice`.
 */
export const ambush: CardEffect = {
  duringChoice: (e) => {
    const combat = e.ctx.state.combat;
    if (!combat || e.opponentPlayer === null) return;
    const cardId = discardRandom(e.ctx, e.opponentPlayer, true);
    if (!cardId) return;
    const side = e.role === 'defender' ? 'defender' : 'attacker';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += cardDef(e.ctx, cardId).boost;
  },
};

/**
 * „Draw 1 card, then choose an opponent. They may choose to discard 1 card.
 * If they do not, draw 1 more card."
 */
const stealFromTheRich: CardEffect = {
  scheme: (e) => {
    drawCard(e.ctx, e.player);
    const opponent = opponentOf(e);
    if (opponent === null) return;
    if ((e.ctx.state.players[opponent]?.hand.length ?? 0) === 0) {
      drawCard(e.ctx, e.player);
      return;
    }
    askChoice(e.ctx, {
      kind: 'confirm',
      player: opponent,
      source: e.cardId,
      label: 'Steal from the Rich: odhodíš 1 kartu? Ak nie, Robin si potiahne ďalšiu.',
      optional: true,
      effect: STEAL,
      yes: 'Odhodiť 1 kartu',
      no: 'Nie — Robin ťahá',
      onSkip: { kind: 'effect', effect: STEAL, data: String(e.player) },
    });
  },
  onConfirm: (ctx, choice) => {
    askChoice(ctx, {
      kind: 'card',
      player: choice.player,
      source: choice.source,
      label: 'Steal from the Rich: odhoď 1 kartu',
      optional: false,
      from: 'hand',
      purpose: 'discard',
      discards: true,
    });
  },
  onFollow: (ctx, _choice, robinPlayer) => {
    drawCard(ctx, Number(robinPlayer));
  },
};

/** „Draw a number of cards equal to the amount of damage dealt to the opposing fighter." */
export const disarmingShot: CardEffect = {
  after: (e) => {
    for (let i = 0; i < (e.damage ?? 0); i++) drawCard(e.ctx, e.player);
  },
};

/** „Draw 1 card. Return a defeated Outlaw (if any) to any space in Robin Hood's zone." */
const defendersOfSherwood: CardEffect = {
  after: (e) => {
    drawCard(e.ctx, e.player);
    const robin = e.ctx.state.fighters.find((f) => f.owner === e.player && f.defId === ROBIN);
    const fallen = (e.ctx.state.defeated ?? []).find((f) => f.owner === e.player && f.defId === OUTLAW);
    if (!robin || !fallen) return;
    const map = getMap(e.ctx);
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Defenders of Sherwood: vráť porazeného Outlawa do Robinovej zóny',
      optional: false,
      fighterId: fallen.id,
      spaces: map.spaces
        .map((s) => s.id)
        .filter((id) => shareZone(map, id, robin.spaceId) && !e.ctx.state.fighters.some((f) => f.spaceId === id)),
    });
  },
};

/** „IMMEDIATELY: Cancel all effects on your opponent's card and ignore its defense value." */
const highwayRobbery: CardEffect = {
  cancelsOpponent: true,
  immediate: (e) => {
    const combat = e.ctx.state.combat;
    if (e.role === 'attacker' && combat) (combat.flags ??= []).push('zeroDefense');
  },
};

/** „If your fighter is adjacent to the opposing fighter, draw 1 card." */
export const snark: CardEffect = {
  after: (e) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    const them = fighterOrNull(e.ctx, e.opponentFighterId);
    if (me && them && neighbors(getMap(e.ctx), me.spaceId).includes(them.spaceId)) drawCard(e.ctx, e.player);
  },
};

/** „Deal 1 damage to each opposing fighter adjacent to your fighter." */
export const wilyFighting: CardEffect = {
  after: (e) => {
    const me = fighterOrNull(e.ctx, e.fighterId);
    if (!me) return;
    const around = new Set(neighbors(getMap(e.ctx), me.spaceId));
    const targets = e.ctx.state.fighters
      .filter((f) => areEnemies(e.ctx.state, f.owner, e.player) && around.has(f.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(e.ctx, id, 1);
  },
};

export const robinHoodCards: EffectRegistry = {
  'robin-hood.ambush': ambush,
  [STEAL]: stealFromTheRich,
  'robin-hood.disarming-shot': disarmingShot,
  'robin-hood.piercing-shot': draw(2),
  'robin-hood.defenders-of-sherwood': defendersOfSherwood,
  'robin-hood.highway-robbery': highwayRobbery,
  'robin-hood.snark': snark,
  'robin-hood.wily-fighting': wilyFighting,
  'robin-hood.feint': feint,
  'robin-hood.regroup': regroup,
  // A Hunter's Eye: bez efektu.
};

export const robinHoodSpecial: HeroSpecial = {
  name: 'Hit and Run',
  /** „After you attack, you may move your attacking fighter up to 2 spaces." */
  afterCombat: (ctx: Ctx, player) => {
    const combat = ctx.state.combat;
    if (combat?.attackerPlayer !== player) return;
    const attacker = fighterOrNull(ctx, combat.attackerFighter);
    if (!attacker) return;
    askMove(
      { ctx, cardId: combat.attackCard ?? '', player, fighterId: attacker.id, role: 'attacker', opponentPlayer: null, opponentFighterId: null, won: false },
      { fighters: [attacker.id], max: 2, label: `Hit and Run: smieš presunúť ${attacker.name} až o 2` },
    );
  },
};
